import { useEffect } from 'react'
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom'
import { PageTransition } from './components/layout/PageTransition'
import { ThemeProvider } from "./components/theme-provider"
import { HelmetProvider } from 'react-helmet-async'
import { Header } from './components/layout/Header'
import { RepuestosHeader } from './components/layout/RepuestosHeader'
import { Footer } from './components/layout/Footer'
import Home from './pages/Home'
import './index.css'
import { getVehicles } from '@/hooks/getVehicles'
import { getBrands } from './hooks/getBrands'
import { getCategories } from './hooks/getCategories'
import Dealers from './pages/Dealers'
import Catalog from './pages/Catalog'
import Financing from './pages/Financing'
import AboutPage from './pages/About'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Warranty from './pages/Warranty'
import Returns from './pages/Returns'
import Repuestos from './pages/Repuestos'
import QuotePage from './pages/Repuestos/QuotePage'
import Soporte from './pages/Soporte'
import VehiclePage from './pages/Vehicle'
import { useAuthState } from './context/useAuthState'
import { FIREBASE_AUTH } from './firebase'
import { onAuthStateChanged } from 'firebase/auth'
import LoginPage from './pages/Auth/LoginPage'
import SignupPage from './pages/Auth/SignupPage'
import { useGlobalState } from './hooks/context/global/useGlobalState'
import { usePartsState } from './hooks/context/global/usePartsState'
import { SupportButton } from './components/Support/SupportButton'

function App() {
  const { vehicles, setVehicles, brands, setBrands, categories, setCategories } = useGlobalState()
  const { parts, fetchParts, fetchPartCategories, fetchPartBrands } = usePartsState()
  const { setUser, setLoading } = useAuthState()

  // Handle authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, (user) => {
      setLoading(true)
      if (user) {
        setUser(user)
      } else {
        setUser(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [setUser, setLoading])

  useEffect(() => {
    const fetchData = async () => {
      if (!vehicles || vehicles.length === 0) {
        const vehiclesData = await getVehicles()
        setVehicles(vehiclesData)
      }

      if (!brands || brands.length === 0) {
        const brandsData = await getBrands()
        setBrands(brandsData)
      }

      if (!categories || categories.length === 0) {
        const categoriesData = await getCategories()
        setCategories(categoriesData)
      }
    }
    fetchData()
  }, [vehicles, brands, categories, setVehicles, setBrands, setCategories])

  useEffect(() => {
    if (parts.length > 0) return
    const loadParts = async () => {
      await fetchPartBrands()
      await fetchPartCategories()
      await fetchParts()
    }
    loadParts()
  }, [parts.length, fetchParts, fetchPartCategories, fetchPartBrands])

  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  )
}

function AppContent() {
  const location = useLocation()
  const isCatalogLayout = location.pathname.startsWith('/repuestos') || location.pathname === '/catalogo'
  const isImmersiveLayout = location.pathname === '/sucursales'
  const isAuthPage = location.pathname === '/login' || location.pathname === '/registro'

  // Reinicia el scroll al inicio en cada cambio de ruta.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Auth pages render as standalone fullscreen layouts
  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registro" element={<SignupPage />} />
      </Routes>
    )
  }

  return (
    <div className={`flex flex-col min-h-screen bg-background text-foreground ${isImmersiveLayout ? 'overflow-hidden' : 'overflow-x-hidden'}`}>
      {/* Header — fixed, wrapper colapsa su espacio en el flujo */}
      {isImmersiveLayout ? (
        <Header />
      ) : (
        <div className={isCatalogLayout ? 'h-0 w-full' : 'h-0 w-full -mb-10 lg:-mb-14'}>
          {isCatalogLayout ? <RepuestosHeader /> : <Header />}
        </div>
      )}

      {/* Content */}
      {isImmersiveLayout ? (
        <Routes>
          <Route path="/sucursales" element={<Dealers />} />
        </Routes>
      ) : (
        <>
          <main className={`flex flex-1 flex-col min-h-0 ${isCatalogLayout ? 'pt-16 lg:pt-20' : 'pt-24 lg:pt-28'}`}>
            <PageTransition locationKey={location.pathname}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/catalogo" element={<Catalog />} />
                <Route path="/vehiculo/:id" element={<VehiclePage />} />
                <Route path="/financiamiento" element={<Financing />} />
                <Route path="/nosotros" element={<AboutPage />} />
                <Route path="/privacidad" element={<Privacy />} />
                <Route path="/terminos" element={<Terms />} />
                <Route path="/garantia" element={<Warranty />} />
                <Route path="/devoluciones" element={<Returns />} />
                <Route path="/repuestos" element={<Repuestos />} />
                <Route path="/repuestos/cotizacion" element={<QuotePage />} />
                <Route path="/soporte" element={<Soporte />} />
              </Routes>
            </PageTransition>
          </main>
          <Footer />
        </>
      )}

      {!isImmersiveLayout && !isCatalogLayout && <SupportButton />}
    </div>
  )
}

export default App

